<template>
  <ListViewBanner :add-route="{ name: 'app_district_new' }">
    Districts
  </ListViewBanner>

  <DistrictSearch @search="searchParams = $event" />

  <AppContainer>
    <AppTable>
      <AppTableHeader>
        <th>Name</th>
        <th>Province</th>
        <th class="text-right">
          No. of Users
        </th>
        <th class="text-right">
          No. of Plans
        </th>
        <th>
          <span class="sr-only">Actions</span>
        </th>
      </AppTableHeader>

      <AppTableBody
        :loading="loading"
        :no-results="!districts.length"
        :colspan="4"
      >
        <template v-if="districts.length">
          <tr
            v-for="district in districts"
            :key="district.id"
            class="overflow-visible"
          >
            <td>
              {{ district.name }}

              <template v-if="district.archived">
                <BadgeArchived />
              </template>
            </td>

            <td>
              <template v-if="district?.province">
                {{ district.province.name }}
              </template>

              <span v-else>-</span>
            </td>

            <td class="text-right">
              {{ district.userCount }}
            </td>
            <td class="text-right">
              {{ district.planCount }}
            </td>

            <td>
              <template v-if="district.archived">
                <RestoreButton @click="entityToRestore = district" />
              </template>

              <template v-else>
                <AppDropdown>
                  <AppDropdownItem
                    text="Edit"
                    :to="{ name: 'app_district_edit', params: { district: district.id } }"
                  />

                  <AppDropdownItem
                    text="Archive"
                    @click="entityToArchive = district"
                  />
                </AppDropdown>
              </template>
            </td>
          </tr>
        </template>
      </AppTableBody>
    </AppTable>

    <AppPagination
      v-model:itemsPerPage="paginationData.itemsPerPage"
      v-model:currentPage="paginationData.page"
      :items-per-page="paginationData?.itemsPerPage"
      :current-page="paginationData?.page"
      :total-items="paginationData?.total"
      @page-change="searchParams.page = $event"
      @items-per-page-change="searchParams.itemsPerPage = $event"
    />
  </AppContainer>

  <!-- Archive Modal -->
  <template v-if="entityToArchive">
    <ConfirmationModal
      title="Archive District"
      :processing="archiving"
      @confirm="archiveEntity"
      @close="entityToArchive = null"
    >
      <p class="mb-0.5">
        <span>Name: {{ entityToArchive?.name }}</span>
      </p>

      <p>
        Are you sure you want to <strong>ARCHIVE</strong> this District?
      </p>
    </ConfirmationModal>
  </template>

  <!-- Restore Modal -->
  <template v-if="entityToRestore">
    <ConfirmationModal
      title="Restore District"
      :processing="restoring"
      @confirm="restoreEntity"
      @close="entityToRestore = null"
    >
      <p class="mb-0.5">
        <span>Name: {{ entityToRestore?.name }}</span>
      </p>

      <p>
        Are you sure you want to <strong>RESTORE</strong> this District?
      </p>
    </ConfirmationModal>
  </template>
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import { ref, watchEffect } from 'vue';

import { useHttp, useEntityArchiver, useEntityRestorer } from '@/composables';

import { AppContainer, AppPagination } from '@/components/app';
import { BadgeArchived } from '@/components/badges';
import { ListViewBanner } from '@/components/banners';
import { AppDropdown, AppDropdownItem } from '@/components/dropdowns';
import { DistrictSearch } from '@/components/forms/search';
import { AppTable, AppTableBody, AppTableHeader } from '@/components/tables';
import { ConfirmationModal } from '@/components/modals/index.js';
import { RestoreButton } from '@/components/buttons/index.js';

const loading = ref();
const districts = ref([]);
const paginationData = ref({
  page: 1,
  itemsPerPage: 10,
});
const searchParams = ref({
  page: 1,
});

const loadDistricts = async () => {
  loading.value = true;
  districts.value = [];

  const response = await useHttp().get('/districts', searchParams.value);

  if (response.statusCode === HttpStatusCode.Ok) {
    districts.value = response.data.items;
    paginationData.value = response.data.pagination;
  }

  loading.value = false;
};

const { archiving, entityToArchive, archiveEntity } = useEntityArchiver('districts', loadDistricts);
const { restoring, entityToRestore, restoreEntity } = useEntityRestorer('districts', loadDistricts);

watchEffect(loadDistricts);
</script>
